import { pointCalc, anomalyCalc, axleCalc, diagonalCalc, deviationLabel, fmtSigned, fmtAbs, fmtTol, fmtCoord, fmtPct, SIT } from "../../lib/calc";
import { BRAND, CLASS_STYLE } from "../../lib/constants";
import { finalClass } from "../../lib/laudo";
import { fmtDay, visualList } from "../../lib/model";
import ChassisSvg from "../map/ChassisSvg";
import { PhotoOverlay } from "../photos/PhotoAnnotator";

const td = "border border-slate-300 px-1.5 py-1 align-top";
const thc = "border border-slate-300 px-1.5 py-1 bg-[#050B10] text-[#FFD500] text-left font-bold";

function Sec({ n, title, children }) {
  return (
    <section className="mt-6 break-inside-avoid-page">
      <h3 className="flex items-center gap-2 font-display font-extrabold uppercase tracking-wide text-[15px] text-[#050B10] border-b-2 border-[#050B10] pb-1 mb-2">
        <span className="h-4 w-1.5 bg-[#FFD500]" />{n}. {title}
      </h3>
      {children}
    </section>
  );
}

function T({ head, rows, testId }) {
  return (
    <table className="w-full border-collapse text-[10.5px] font-mono" data-testid={testId}>
      <thead><tr>{head.map((h, i) => <th key={i} className={thc}>{h}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => <tr key={i} className={i % 2 ? "bg-slate-50" : ""}>{r.map((c, j) => <td key={j} className={td}>{c}</td>)}</tr>)}</tbody>
    </table>
  );
}

const red = (txt, on) => (on ? <span className="text-[#FF0000] font-bold">{txt}</span> : txt);

export function LaudoDocument({ insp, photos, analysis, laudo }) {
  const v = insp.vehicle || {};
  const r = insp.reference || {};
  const final = finalClass(analysis);
  const rows = (insp.points || []).map((p) => ({ p, c: pointCalc(p, r) }));
  const sig = insp.signature || {};
  const visualAnom = visualList(insp.visual).filter((x) => x.status === "ANOMALIA");
  const diag = diagonalCalc(insp.diagonals, r);
  const axles = (insp.axles || []).map((a) => ({ a, c: axleCalc(a, r) })).filter((o) => o.c.hasData);
  const recs = (laudo.recomendacoes || "").split("\n").filter(Boolean);

  return (
    <div className="bg-white text-slate-800 shadow-2xl shadow-black/60 mx-auto w-full max-w-[860px]" data-testid="laudo-document">
      <div className="bg-[#050B10] px-8 py-5 flex justify-between items-center">
        <div>
          <div className="font-display font-black text-2xl tracking-wider text-white">NILLO <span className="text-[#FFD500]">EQUIPAMENTOS</span></div>
          <div className="font-mono text-[9px] tracking-[0.2em] text-slate-400">{BRAND.slogan} · {BRAND.telefone} · {BRAND.atendimento}</div>
        </div>
        <div className="text-right font-mono text-[10px] text-slate-300">
          <div className="text-white font-bold">RELATÓRIO DIGITAL DE ALINHAMENTO DE CHASSIS</div>
          <div>PLACA {v.placa || "—"} · OS {v.numero_ordem_servico || "—"} · {fmtDay(v.data_inspecao)}</div>
        </div>
      </div>
      <div className="h-1.5 hazard-stripe" />
      <div className="px-8 py-6">
        <h2 className="font-display font-black uppercase text-3xl text-[#050B10]">Laudo técnico de alinhamento de chassis</h2>
        <div className="mt-3 flex flex-wrap items-center gap-3 border-2 px-4 py-3" style={{ borderColor: final ? `rgb(${CLASS_STYLE[final].rgb.join(",")})` : "#94A3B8" }}>
          <span className="font-mono text-[10px] tracking-widest text-slate-500">RESULTADO DO ALINHAMENTO</span>
          <span className="font-mono font-bold text-lg" style={{ color: final ? `rgb(${CLASS_STYLE[final].rgb.join(",")})` : "#64748B" }} data-testid="laudo-resultado">{final || analysis.statusMsg}</span>
        </div>

        <Sec n={1} title="Identificação do veículo">
          <T head={["Campo", "Valor", "Campo", "Valor"]} rows={[
            ["Placa", v.placa || "—", "Cliente", v.cliente || "—"], ["Marca", v.marca || "—", "Modelo", v.modelo || "—"],
            ["Ano", v.ano || "—", "Cor", v.cor || "—"], ["Tipo", v.tipo_veiculo === "Outro" ? v.tipo_outro || "Outro" : v.tipo_veiculo || "—", "Nº OS", v.numero_ordem_servico || "—"],
            ["Data", fmtDay(v.data_inspecao), "Hora", v.hora_inspecao || "—"], ["Executor", v.executor || "—", "Empresa", BRAND.empresa],
          ]} />
        </Sec>
        <Sec n={2} title="Objetivo da inspeção"><p className="text-[12px] leading-relaxed">{laudo.objetivo}</p></Sec>
        <Sec n={3} title="Metodologia de medição"><p className="text-[12px] leading-relaxed">{laudo.metodologia}</p></Sec>
        <Sec n={4} title="Pontos de referência utilizados">
          <p className="text-[12px] leading-relaxed mb-2">{laudo.pontos_referencia}</p>
          <T head={["Parâmetro", "Valor"]} rows={[["Tolerância padrão (pontos)", fmtTol(r.tolerancia_padrao ? Math.abs(Number(String(r.tolerancia_padrao).replace(",", "."))) : null)], ["Tolerância diagonais", r.tolerancia_diagonal ? `±${r.tolerancia_diagonal} mm` : SIT.SEM_TOL], ["Tolerância eixos", r.tolerancia_eixo ? `±${r.tolerancia_eixo} mm` : SIT.SEM_TOL], ["Tolerância longarinas", r.tolerancia_longarina ? `±${r.tolerancia_longarina} mm` : SIT.SEM_TOL], ["Fonte", analysis.fontes]]} />
        </Sec>
        <Sec n={5} title="Tabela de medições">
          <T testId="laudo-tabela-medicoes" head={["Nº", "Descrição", "Local", "Nominal", "Encontr.", "Dif.", "Result.", "Tol.", "Situação"]} rows={rows.map(({ p, c }) => [p.numero, p.descricao || "—", p.localizacao || "—", p.nominal || "—", p.encontrada || "—", fmtSigned(c.diff), fmtAbs(c.resultant), fmtTol(c.tol), red(c.situacao, c.situacao === SIT.FORA)])} />
          {axles.length > 0 && <div className="mt-2"><T head={["Eixo", "Esq.", "Centro", "Dir.", "Dif. E-D", "Desloc.", "Situação"]} rows={axles.map(({ a, c }) => [a.eixo, a.esquerda || "—", a.centro || "—", a.direita || "—", fmtSigned(c.diffLR), fmtSigned(c.desl), red(c.situacao, c.situacao === SIT.FORA)])} /></div>}
          {diag.rows.length > 0 && <div className="mt-2"><T head={["Diagonal", "De", "Até", "Medida", "Referência", "Dif."]} rows={diag.rows.map((d) => [d.nome, d.ponto_inicial || "—", d.ponto_final || "—", d.medida || "—", d.referencia || "—", fmtSigned(d.diff)])} /></div>}
        </Sec>
        <Sec n={6} title="Anomalias encontradas">
          {(insp.anomalies || []).length ? (
            <T head={["Nº", "Tipo", "Localização", "ΔX", "ΔY", "ΔZ", "Total", "Sentido", "Gravidade"]} rows={insp.anomalies.map((a) => { const c = anomalyCalc(a); return [red(a.numero, true), a.tipo || "—", a.localizacao || "—", fmtSigned(c.dx), fmtSigned(c.dy), fmtSigned(c.dz), fmtAbs(c.total), a.sentido || "—", a.gravidade || "—"]; })} />
          ) : <p className="text-[12px]">Nenhuma anomalia registrada no formulário.</p>}
          {visualAnom.length > 0 && <p className="text-[12px] mt-2"><b>Inspeção visual:</b> {visualAnom.map((x) => `${x.item}${x.obs ? ` (${x.obs})` : ""}`).join("; ")}.</p>}
        </Sec>
        <Sec n={7} title="Representação gráfica dos desvios">
          <div className="border border-slate-300"><ChassisSvg insp={insp} view="top" theme="light" /></div>
          <div className="border border-slate-300 mt-2"><ChassisSvg insp={insp} view="side" theme="light" /></div>
        </Sec>
        <Sec n={8} title="Coordenadas dos pontos">
          <T head={["Nº", "Ref. X", "Ref. Y", "Ref. Z", "Enc. X", "Enc. Y", "Enc. Z", "ΔX", "ΔY", "ΔZ", "Identificação"]} rows={rows.map(({ p, c }) => {
            const fora = c.situacao === SIT.FORA;
            return [p.numero, fmtCoord(p.ref?.x), fmtCoord(p.ref?.y), fmtCoord(p.ref?.z), fmtCoord(p.found?.x), fmtCoord(p.found?.y), fmtCoord(p.found?.z), red(fmtSigned(c.dx), fora), red(fmtSigned(c.dy), fora), red(fmtSigned(c.dz), fora), red(deviationLabel(p.numero, c).text, true)];
          })} />
        </Sec>
        <Sec n={9} title="Análise das diferenças">
          <p className="text-[12px] mb-2 font-mono">Pontos medidos: {analysis.counts.medidos} · Fora: {analysis.counts.fora} · Atenção: {analysis.counts.atencao} · Maior desvio: {analysis.maior ? `${fmtAbs(analysis.maior.valor)} (${analysis.maior.ponto})` : "—"} · Conformidade: {fmtPct(analysis.conformidade)}</p>
          <ul className="list-disc pl-5 text-[12px] space-y-1">{analysis.findings.map((f, i) => <li key={i} className={f.nivel === "fora" ? "text-[#FF0000]" : ""}>{f.texto}</li>)}</ul>
          {analysis.warnings.length > 0 && <ul className="list-disc pl-5 text-[12px] space-y-1 mt-2 text-amber-700">{analysis.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>}
        </Sec>
        <Sec n={10} title="Conclusão técnica"><p className="text-[12px] leading-relaxed" data-testid="laudo-conclusao">{laudo.conclusao}</p></Sec>
        <Sec n={11} title="Recomendações">
          <ul className="list-disc pl-5 text-[12px] space-y-1">{recs.map((x, i) => <li key={i}>{x}</li>)}</ul>
          {laudo.observacoes && <p className="text-[12px] mt-2"><b>Observações complementares:</b> {laudo.observacoes}</p>}
        </Sec>
        <Sec n={12} title="Registro fotográfico">
          {!photos.length && <p className="text-[12px]">Sem registro fotográfico.</p>}
          <div className="grid grid-cols-2 gap-3">
            {photos.map((p, i) => (
              <figure key={p.id} className="border border-slate-300 break-inside-avoid">
                <div className="relative bg-black" style={{ aspectRatio: `${p.width} / ${p.height}` }}>
                  <img src={p.image} alt={p.descricao || `Foto ${i + 1}`} className="absolute inset-0 w-full h-full object-contain" />
                  <PhotoOverlay photo={p} />
                </div>
                <figcaption className="p-2 text-[10.5px]"><b>Foto {i + 1}</b> · {p.local || "—"}{p.anomalia ? ` · Anomalia ${p.anomalia}` : ""}<br />{p.descricao}{p.observacao ? ` — ${p.observacao}` : ""}</figcaption>
              </figure>
            ))}
          </div>
        </Sec>
        <Sec n={13} title="Assinatura do executor">
          <div className="flex flex-col items-start">
            {sig.image ? <img src={sig.image} alt="Assinatura" className="h-24" data-testid="laudo-assinatura-img" /> : <div className="h-24 flex items-end text-slate-400 text-xs">(assinatura pendente)</div>}
            <div className="w-72 border-t border-slate-800 pt-1 text-[12px]">
              <b>{sig.executor || v.executor || "—"}</b><br />Registro profissional: {sig.registro_profissional || "—"}<br />{BRAND.empresa} · {fmtDay(sig.data)}
            </div>
          </div>
        </Sec>
      </div>
      <div className="bg-[#050B10] px-8 py-3 flex flex-wrap justify-between gap-2 font-mono text-[9px] text-slate-400">
        <span>{BRAND.empresa} · {BRAND.atividade}</span>
        <span>WHATSAPP {BRAND.telefone} · {BRAND.atendimentoRodape}</span>
        <span className="text-[#FFD500] w-full text-center font-bold tracking-widest">{BRAND.frase}</span>
      </div>
    </div>
  );
}
